/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { useNextRouter } from '@/data/Content/_NextRouter';
import { productFetcher } from '@/data/Content/_Product';
import { getContractIdParamFromContext, useSettings } from '@/data/Settings';
import { useUser } from '@/data/User';
import { ProductType } from '@/data/types/Product';
import { extractContentsArray } from '@/data/utils/extractContentsArray';
import { getClientSideCommon } from '@/data/utils/getClientSideCommon';
import { expand, shrink } from '@/data/utils/keyUtil';
import { RequestParams } from 'integration/generated/query/http-client';
import { GetServerSidePropsContext } from 'next';
import useSWR from 'swr';

const DATA_KEY = "Products";

const fetcher =
	(pub: boolean, context?: GetServerSidePropsContext) =>
		async (
			query: {
				storeId: string;
				catalogId: string;
				[key: string]: string | boolean | (string | number)[];
			},
			params: RequestParams
		): Promise<ProductType[] | []> => {
			const response = await productFetcher(pub, context)(query, params);
			const products = extractContentsArray(response);
			return products ? products : [];
		};

export const useProducts = (id:string[], idType:string) => {
	const router = useNextRouter();
	const { settings } = useSettings();
	const { user } = useUser();
	const currentContract = getContractIdParamFromContext(user?.context);
	const contract = currentContract;
	const {
		storeId,
		defaultCatalogId: catalogId,
		defaultCurrency: currency,
		langId,
	} = getClientSideCommon(settings, router);
	const { data, error, isLoading } = useSWR(
		storeId && id && id.length>0
			? [
					shrink({
						storeId,
						id:idType==='id'?id:undefined,
						partNumber:idType==='partNumber'?id:undefined,
						catalogId,
						langId,
						currency,
						...contract,
					}),
					DATA_KEY,
			  ]
			: null,
		async ([props]) => fetcher(true)(expand(props), {})
	);
	return {
		products: data,
		loading: id && !error && isLoading,
		error,
	};
};
